// =====================================================
// DOCUMENT TYPES ADMIN PAGE
// Tipos de documento para presupuestos — Catálogos Maestros
// =====================================================
import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  FileText, Plus, Search, Filter, RefreshCw, Edit2, Power,
  Loader2, Save, X, AlertCircle, ChevronLeft, ChevronRight, Building2,
} from 'lucide-react';
import { documentTypesApi, type DocumentType, type CreateDocumentTypeRequest, type UpdateDocumentTypeRequest } from '../../api/document-types';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { toast } from '../../components/ui/Toast';

interface FormData {
  code: string;
  name: string;
  requiresCustomTitle: boolean;
  sortOrder: number;
}

export function DocumentTypesAdminPage() {
  const [items, setItems] = useState<DocumentType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<DocumentType | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean; item: DocumentType | null; action: 'activate' | 'deactivate';
  }>({ isOpen: false, item: null, action: 'deactivate' });
  const [isProcessing, setIsProcessing] = useState(false);

  const [assignmentModal, setAssignmentModal] = useState<{
    isOpen: boolean; item: DocumentType | null;
    sites: { id: string; code: string; name: string; isAssigned: boolean }[];
    selectedIds: string[]; isLoading: boolean; isSaving: boolean;
  }>({ isOpen: false, item: null, sites: [], selectedIds: [], isLoading: false, isSaving: false });

  const [bulkModal, setBulkModal] = useState<{
    isOpen: boolean;
    sites: { id: string; code: string; name: string }[];
    selectedSiteId: string;
    allTypes: DocumentType[];
    selectedTypeIds: string[];
    isLoadingSites: boolean;
    isLoadingTypes: boolean;
    isSaving: boolean;
  }>({
    isOpen: false, sites: [], selectedSiteId: '', allTypes: [],
    selectedTypeIds: [], isLoadingSites: false, isLoadingTypes: false, isSaving: false,
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: { code: '', name: '', requiresCustomTitle: false, sortOrder: 0 },
  });

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await documentTypesApi.getAll(showInactive);
      setItems(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar tipos de documento');
    } finally {
      setIsLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = items.filter(i =>
    !searchQuery ||
    i.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    i.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleCreate = () => {
    setEditingItem(null); setFormError(null);
    reset({ code: '', name: '', requiresCustomTitle: false, sortOrder: 0 });
    setIsModalOpen(true);
  };

  const handleEdit = (item: DocumentType) => {
    setEditingItem(item); setFormError(null);
    reset({ code: item.code, name: item.name, requiresCustomTitle: item.requiresCustomTitle, sortOrder: item.sortOrder });
    setIsModalOpen(true);
  };

  const handleSave = async (formData: FormData) => {
    setFormError(null);
    setIsSaving(true);
    try {
      if (editingItem) {
        await documentTypesApi.update(editingItem.id, formData as UpdateDocumentTypeRequest);
        toast.success('Actualizado', `Tipo "${formData.name}" actualizado`);
      } else {
        await documentTypesApi.create(formData as CreateDocumentTypeRequest);
        toast.success('Creado', `Tipo "${formData.name}" creado`);
      }
      setIsModalOpen(false);
      setEditingItem(null);
      loadData();
    } catch (err: any) {
      setFormError(err.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = (item: DocumentType) => {
    setConfirmDialog({ isOpen: true, item, action: item.isActive ? 'deactivate' : 'activate' });
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.item) return;
    setIsProcessing(true);
    try {
      if (confirmDialog.action === 'deactivate') {
        await documentTypesApi.deactivate(confirmDialog.item.id);
        toast.success('Desactivado', `"${confirmDialog.item.name}" desactivado`);
      } else {
        await documentTypesApi.reactivate(confirmDialog.item.id);
        toast.success('Activado', `"${confirmDialog.item.name}" activado`);
      }
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message);
    } finally {
      setIsProcessing(false);
      setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' });
    }
  };

  const handleOpenAssignment = async (item: DocumentType) => {
    setAssignmentModal({ isOpen: true, item, sites: [], selectedIds: [], isLoading: true, isSaving: false });
    try {
      const sites = await documentTypesApi.getSiteAssignments(item.id);
      setAssignmentModal(prev => ({
        ...prev, sites, selectedIds: sites.filter(s => s.isAssigned).map(s => s.id), isLoading: false,
      }));
    } catch {
      setAssignmentModal(prev => ({ ...prev, isLoading: false }));
      toast.error('Error', 'No se pudieron cargar los centros');
    }
  };

  const handleSaveAssignment = async () => {
    if (!assignmentModal.item) return;
    setAssignmentModal(prev => ({ ...prev, isSaving: true }));
    try {
      await documentTypesApi.updateSiteAssignments(assignmentModal.item.id, assignmentModal.selectedIds);
      toast.success('Guardado', 'Asignación de centros actualizada');
      setAssignmentModal({ isOpen: false, item: null, sites: [], selectedIds: [], isLoading: false, isSaving: false });
      await loadData();
    } catch {
      toast.error('Error', 'No se pudo guardar la asignación');
      setAssignmentModal(prev => ({ ...prev, isSaving: false }));
    }
  };

  const handleOpenBulkModal = async () => {
    setBulkModal(prev => ({ ...prev, isOpen: true, isLoadingSites: true, isLoadingTypes: false }));
    try {
      const token = localStorage.getItem('imi_access_token') || '';
      const r = await fetch('/api/v1/sites/select', { headers: { Authorization: 'Bearer ' + token } });
      const sites = r.ok ? await r.json() : [];
      const allTypes = await documentTypesApi.getAll(false);
      const firstSiteId = sites[0]?.id || '';
      let selectedTypeIds: string[] = [];
      if (firstSiteId) {
        selectedTypeIds = await documentTypesApi.getAssignedTypeIds(firstSiteId);
      }
      setBulkModal(prev => ({
        ...prev,
        sites,
        allTypes,
        isLoadingSites: false,
        selectedSiteId: firstSiteId,
        selectedTypeIds,
      }));
    } catch {
      toast.error('Error', 'No se pudieron cargar los datos');
      setBulkModal(prev => ({ ...prev, isLoadingSites: false, isOpen: false }));
    }
  };

  const handleBulkSiteChange = async (siteId: string) => {
    setBulkModal(prev => ({ ...prev, selectedSiteId: siteId, isLoadingTypes: true }));
    try {
      const ids = await documentTypesApi.getAssignedTypeIds(siteId);
      setBulkModal(prev => ({ ...prev, selectedTypeIds: ids, isLoadingTypes: false }));
    } catch {
      setBulkModal(prev => ({ ...prev, isLoadingTypes: false }));
    }
  };

  const handleBulkSave = async () => {
    if (!bulkModal.selectedSiteId) return;
    setBulkModal(prev => ({ ...prev, isSaving: true }));
    try {
      await documentTypesApi.bulkAssignToSite(bulkModal.selectedSiteId, bulkModal.selectedTypeIds);
      await loadData();
      setBulkModal({ isOpen: false, sites: [], selectedSiteId: '', allTypes: [], selectedTypeIds: [], isLoadingSites: false, isLoadingTypes: false, isSaving: false });
      toast.success('Guardado', 'Asignación masiva actualizada');
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo guardar la asignación');
      setBulkModal(prev => ({ ...prev, isSaving: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tipos de Documento</h1>
          <p className="text-sm text-gray-500 mt-1">Tipos de documentos adjuntables a presupuestos aceptados</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center flex-1 max-w-2xl gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text" value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
                placeholder="Buscar por código o nombre..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
            <button onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${showFilters ? 'bg-blue-50 border-blue-200 text-blue-600' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              <Filter className="h-4 w-4" /> Filtros
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadData} disabled={isLoading} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors" title="Actualizar">
              <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={handleOpenBulkModal}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors">
              <Building2 className="h-4 w-4" /> Asignar a centro
            </button>
            <button onClick={handleCreate}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 shadow-md shadow-blue-100 transition-colors">
              <Plus className="h-4 w-4" /> Nuevo tipo
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="flex items-center gap-6 border-t border-gray-100 pt-3">
            <label className="inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={showInactive} onChange={e => { setShowInactive(e.target.checked); setPage(1); }}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
              <span className="ml-2 text-sm text-gray-600">Mostrar inactivos</span>
            </label>
          </div>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {paginated.length === 0 && !isLoading ? (
          <div className="p-12 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">{searchQuery ? 'Sin resultados' : 'No hay tipos de documento'}</h3>
            <p className="text-gray-500">{searchQuery ? 'No se encontraron tipos con ese criterio' : 'Crea tu primer tipo de documento'}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Código</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nombre</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Título personalizado</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Centros</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginated.map(item => (
                    <tr key={item.id} className={`hover:bg-gray-50 ${!item.isActive ? 'bg-gray-50/50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-lg">{item.code}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`font-medium ${item.isActive ? 'text-gray-900' : 'text-gray-500'}`}>{item.name}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {item.requiresCustomTitle
                          ? <span className="inline-flex items-center px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded-full">Requerido</span>
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button onClick={() => handleOpenAssignment(item)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                            (item.sitesCount ?? 0) > 0
                              ? 'text-blue-600 border-blue-200 bg-blue-50 hover:bg-blue-100'
                              : 'text-gray-400 border-gray-200 hover:bg-gray-50 hover:text-gray-600'
                          }`}>
                          <Building2 className="h-3.5 w-3.5" />
                          {(item.sitesCount ?? 0) > 0 ? <span className="font-semibold">{item.sitesCount}</span> : <span>—</span>}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button onClick={() => handleToggleStatus(item)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${item.isActive ? 'bg-blue-600' : 'bg-gray-300'}`}>
                          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${item.isActive ? 'translate-x-5' : 'translate-x-0'}`}>
                            <Power className={`h-3 w-3 absolute top-1 left-1 ${item.isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                          </span>
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button onClick={() => handleEdit(item)} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded">
                          <Edit2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Mostrando {(page - 1) * PAGE_SIZE + 1} a {Math.min(page * PAGE_SIZE, filtered.length)} de {filtered.length}
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage(p => p - 1)} disabled={page === 1} className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"><ChevronLeft className="h-4 w-4" /></button>
                  <span className="text-sm text-gray-700">Página {page} de {totalPages}</span>
                  <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages} className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"><ChevronRight className="h-4 w-4" /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal crear/editar */}
      <Modal isOpen={isModalOpen} onClose={() => !isSaving && setIsModalOpen(false)}
        title={editingItem ? 'Editar tipo de documento' : 'Nuevo tipo de documento'}
        subtitle={editingItem ? `Editando: ${editingItem.name}` : 'Completa los datos'}>
        <form onSubmit={handleSubmit(handleSave)}>
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 flex-1">{formError}</p>
              <button type="button" onClick={() => setFormError(null)} className="p-1 text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
              <input type="text" maxLength={20}
                {...register('code', { required: 'El código es obligatorio', maxLength: { value: 20, message: 'Máximo 20 caracteres' } })}
                className={`w-full px-4 py-2.5 border rounded-xl text-sm uppercase ${errors.code ? 'border-red-300' : 'border-gray-200'}`}
                placeholder="DOC001" />
              {errors.code && <p className="mt-1 text-xs text-red-500">{errors.code.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
              <input type="text" maxLength={100}
                {...register('name', { required: 'El nombre es obligatorio', maxLength: { value: 100, message: 'Máximo 100 caracteres' } })}
                className={`w-full px-4 py-2.5 border rounded-xl text-sm ${errors.name ? 'border-red-300' : 'border-gray-200'}`}
                placeholder="Consentimiento informado" />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" {...register('requiresCustomTitle')} className="rounded border-gray-300 text-blue-600" />
              <span className="text-sm text-gray-700">Requiere título personalizado</span>
              <span className="text-xs text-gray-400">(el usuario deberá escribir un título al adjuntar)</span>
            </label>
          </div>
          <ModalFooter>
            <button type="button" onClick={() => !isSaving && setIsModalOpen(false)} disabled={isSaving}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
              <X className="h-4 w-4" /> Cancelar
            </button>
            <button type="submit" disabled={isSaving}
              className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2">
              {isSaving ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><Save className="h-4 w-4" />{editingItem ? 'Guardar' : 'Crear'}</>}
            </button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Modal asignación individual */}
        <Modal isOpen={assignmentModal.isOpen}
          onClose={() => !assignmentModal.isSaving && setAssignmentModal({ isOpen: false, item: null, sites: [], selectedIds: [], isLoading: false, isSaving: false })}
          title="Centros asignados"
          subtitle={assignmentModal.item ? `"${assignmentModal.item.name}" estará disponible en los centros seleccionados` : ''}>
          {assignmentModal.isLoading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-gray-300" /></div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {assignmentModal.sites.map(site => (
                <label key={site.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                  <input type="checkbox" checked={assignmentModal.selectedIds.includes(site.id)}
                    onChange={() => setAssignmentModal(prev => ({
                      ...prev,
                      selectedIds: prev.selectedIds.includes(site.id)
                        ? prev.selectedIds.filter(id => id !== site.id)
                        : [...prev.selectedIds, site.id],
                    }))}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                  <Building2 className="h-4 w-4 text-gray-400 shrink-0" />
                  <span className="font-medium text-gray-800 text-sm flex-1 truncate">{site.name}</span>
                  <span className="text-xs text-gray-400 font-mono shrink-0">{site.code}</span>
                  {assignmentModal.selectedIds.includes(site.id) && <span className="text-xs text-blue-600 font-medium shrink-0">Asignado</span>}
                </label>
              ))}
            </div>
          )}
          <ModalFooter>
            <button type="button" onClick={() => setAssignmentModal({ isOpen: false, item: null, sites: [], selectedIds: [], isLoading: false, isSaving: false })}
              disabled={assignmentModal.isSaving}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
              <X className="h-4 w-4" /> Cancelar
            </button>
            <button type="button" onClick={handleSaveAssignment} disabled={assignmentModal.isSaving || assignmentModal.isLoading}
              className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2">
              {assignmentModal.isSaving ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><Save className="h-4 w-4" />Guardar</>}
            </button>
          </ModalFooter>
        </Modal>


      {/* Modal asignación masiva */}
        <Modal isOpen={bulkModal.isOpen}
          onClose={() => !bulkModal.isSaving && setBulkModal(prev => ({ ...prev, isOpen: false }))}
          title="Asignar tipos a un centro"
          subtitle="Selecciona un centro y marca los tipos de documento disponibles">
          {bulkModal.isLoadingSites ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-gray-300" /></div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Centro</label>
                <select value={bulkModal.selectedSiteId} onChange={e => handleBulkSiteChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500">
                  {bulkModal.sites.map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Tipos disponibles
                    {bulkModal.selectedTypeIds.length > 0 && (
                      <span className="ml-2 text-xs text-blue-600">({bulkModal.selectedTypeIds.length} seleccionados)</span>
                    )}
                  </label>
                  <button type="button" onClick={() => setBulkModal(prev => ({
                    ...prev,
                    selectedTypeIds: prev.selectedTypeIds.length === prev.allTypes.length ? [] : prev.allTypes.map(t => t.id),
                  }))} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                    {bulkModal.selectedTypeIds.length === bulkModal.allTypes.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
                  </button>
                </div>
                {bulkModal.isLoadingTypes ? (
                  <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-gray-300" /></div>
                ) : (
                  <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                    {bulkModal.allTypes.map(type => (
                      <label key={type.id} className="flex items-center gap-3 p-2.5 border border-gray-100 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                        <input type="checkbox" checked={bulkModal.selectedTypeIds.includes(type.id)}
                          onChange={() => setBulkModal(prev => ({
                            ...prev,
                            selectedTypeIds: prev.selectedTypeIds.includes(type.id)
                              ? prev.selectedTypeIds.filter(id => id !== type.id)
                              : [...prev.selectedTypeIds, type.id],
                          }))}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                        <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs font-semibold rounded font-mono">{type.code}</span>
                        <span className="text-sm text-gray-800 truncate flex-1">{type.name}</span>
                        {type.requiresCustomTitle && <span className="shrink-0 text-xs text-amber-500 font-medium">req. título</span>}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <ModalFooter>
            <button type="button" onClick={() => setBulkModal(prev => ({ ...prev, isOpen: false }))} disabled={bulkModal.isSaving}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
              <X className="h-4 w-4" /> Cancelar
            </button>
            <button type="button" onClick={handleBulkSave}
              disabled={bulkModal.isSaving || bulkModal.isLoadingSites || bulkModal.isLoadingTypes}
              className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2">
              {bulkModal.isSaving
                ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</>
                : <><Save className="h-4 w-4" />Guardar asignación</>}
            </button>
          </ModalFooter>
        </Modal>


      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => !isProcessing && setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' })}
        onConfirm={handleConfirmToggle}
        isLoading={isProcessing}
        variant={confirmDialog.action === 'deactivate' ? 'warning' : 'info'}
        title={confirmDialog.action === 'deactivate' ? '¿Desactivar tipo?' : '¿Activar tipo?'}
        message={confirmDialog.action === 'deactivate' ? `"${confirmDialog.item?.name}" será desactivado.` : `"${confirmDialog.item?.name}" será reactivado.`}
        confirmText={confirmDialog.action === 'deactivate' ? 'Desactivar' : 'Activar'}
      />
    </div>
  );
}

export default DocumentTypesAdminPage;
